import { signal } from '@angular/core'
import {
  OutcomeKey,
  PostActionKey,
  StepOutcome,
  WorkflowDefinition,
  WorkflowOutputDef,
  WorkflowRunRequest,
  WorkflowScope,
  WorkflowStep
} from './workflow.model'

export interface ActionParamDef {
  key: string
  label: string
  type: 'text' | 'number' | 'select'
  options?: { label: string; value: string }[]
  placeholder?: string
  required?: boolean
}

export interface WorkflowActionDef {
  key: string
  type: string
  scope: WorkflowScope
  technologyRestriction?: string[]
  /** Compatibilità con il vecchio nome del campo. */
  tecnologyRestriction?: string[]
  label: string
  description: string
  icon: string
  outputs: WorkflowOutputDef[]
  params: ActionParamDef[]
}

export interface PostActionDef {
  key: PostActionKey
  label: string
  icon: string
}

export const DEFAULT_OUTPUTS: WorkflowOutputDef[] = [
  { key: 'success', label: 'Success', severity: 'success' },
  { key: 'failed', label: 'Failed', severity: 'failed' }
]

/**
 * Catalogo azioni caricato dal backend (`GET /mediationWorkflow/actions`).
 * Unica fonte di verità: il frontend non contiene più un catalogo statico.
 * È un signal così i computed (validazione, grafo) si aggiornano al load.
 */
const actionCatalog = signal<WorkflowActionDef[]>([])

export function setActionCatalog (actions: WorkflowActionDef[]): void {
  actionCatalog.set(Array.isArray(actions) ? [...actions] : [])
}

export function getActionCatalog (): readonly WorkflowActionDef[] {
  return actionCatalog()
}

export function actionDefByKey (actionKey: string): WorkflowActionDef | undefined {
  return actionCatalog().find(action => action.key === actionKey)
}

export function actionOutputs (actionKey: string): WorkflowOutputDef[] {
  const definition = actionDefByKey(actionKey)
  return definition && definition.outputs && definition.outputs.length
    ? definition.outputs
    : DEFAULT_OUTPUTS
}

export const POST_ACTIONS: PostActionDef[] = [
  { key: 'open-ticket', label: 'Apri ticket', icon: 'fa-solid fa-ticket' },
  { key: 'notify-noc', label: 'Notifica NOC', icon: 'fa-solid fa-bullhorn' },
  { key: 'send-email', label: 'Invia email', icon: 'fa-solid fa-envelope' }
]

let uidCounter = 0

export function uid (prefix = 'id'): string {
  uidCounter += 1
  const cryptoObj = globalThis.crypto
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return `${prefix}-${cryptoObj.randomUUID()}`
  }
  return `${prefix}-${Date.now().toString(36)}-${uidCounter.toString(36)}`
}

export function emptyOutcome (): StepOutcome {
  return { addTags: [], removeTags: [] }
}

export function createStep (
  actionKey: string,
  order: number,
  params: Record<string, unknown> = {}
): WorkflowStep {
  const definition = actionDefByKey(actionKey)

  const defaults: Record<string, unknown> = {}
  ;(definition?.params ?? []).forEach(param => {
    defaults[param.key] = param.type === 'select' && param.options?.length
      ? param.options[0].value
      : ''
  })

  const outcomes: Record<OutcomeKey, StepOutcome> = {}
  actionOutputs(actionKey).forEach(output => { outcomes[output.key] = emptyOutcome() })

  return {
    id: uid('step'),
    order,
    actionKey,
    params: { ...defaults, ...params },
    outcomes,
    postActions: POST_ACTIONS.map(post => ({ key: post.key, enabled: false })),
    transitions: []
  }
}

export function createDefinition (
  name = '',
  fractionIds: string[] = [],
  steps: WorkflowStep[] = []
): WorkflowDefinition {
  return {
    id: uid('wf'),
    name,
    description: '',
    scope: 'single',
    homogeneityRules: ['Tecnologia'],
    entryStepId: null,
    fractionIds: [...fractionIds],
    steps,
    updatedAt: new Date().toISOString()
  }
}

/**
 * Clona un template in una definizione nuova (id freschi, nessuna condivisione
 * di reference) e rimappa le transizioni/entry sui nuovi id step.
 */
export function cloneDefinition (source: WorkflowDefinition, fractionIds: string[]): WorkflowDefinition {
  const idMap = new Map<string, string>()
  source.steps.forEach(step => idMap.set(step.id, uid('step')))

  const steps: WorkflowStep[] = source.steps.map((step, index) => {
    const outcomes: Record<OutcomeKey, StepOutcome> = {}
    Object.entries(step.outcomes ?? {}).forEach(([key, outcome]) => {
      outcomes[key] = {
        addTags: [...(outcome?.addTags ?? [])],
        removeTags: [...(outcome?.removeTags ?? [])]
      }
    })
    // Assicura un outcome per ogni output dichiarato dall'azione.
    actionOutputs(step.actionKey).forEach(output => {
      if (!outcomes[output.key]) outcomes[output.key] = emptyOutcome()
    })

    return {
      ...step,
      id: idMap.get(step.id) ?? uid('step'),
      order: index,
      params: { ...step.params },
      outcomes,
      postActions: step.postActions.map(post => ({ ...post })),
      transitions: (step.transitions ?? []).map(transition => ({
        on: transition.on,
        target: transition.target ? (idMap.get(transition.target) ?? null) : null
      }))
    }
  })

  return {
    id: uid('wf'),
    name: source.name,
    description: source.description,
    scope: source.scope === 'batch' ? 'batch' : 'single',
    homogeneityRules: [...(source.homogeneityRules ?? ['Tecnologia'])],
    entryStepId: source.entryStepId ? (idMap.get(source.entryStepId) ?? null) : null,
    fractionIds: [...fractionIds],
    steps,
    updatedAt: new Date().toISOString()
  }
}

export function validateDefinition (definition: WorkflowDefinition): string[] {
  const errors: string[] = []
  if (!definition.name.trim()) {
    errors.push('Il nome del workflow è obbligatorio.')
  }
  if (!definition.steps.length) {
    errors.push('Aggiungi almeno un’azione al workflow.')
  }
  const emailWithoutRecipients = definition.steps.some(step =>
    step.postActions.some(post =>
      post.key === 'send-email' && post.enabled && parseRecipients(post.recipients).length === 0
    )
  )
  if (emailWithoutRecipients) {
    errors.push('Configura almeno un destinatario per l’invio email.')
  }
  const emailInvalid = definition.steps.some(step =>
    step.postActions.some(post =>
      post.key === 'send-email' && post.enabled &&
      parseRecipients(post.recipients).some(email => !isValidEmail(email))
    )
  )
  if (emailInvalid) {
    errors.push('Uno o più indirizzi email non sono validi.')
  }

  const scope: WorkflowScope = definition.scope === 'batch' ? 'batch' : 'single'
  const stepIds = new Set(definition.steps.map(step => step.id))
  const catalogLoaded = actionCatalog().length > 0

  definition.steps.forEach(step => {
    const action = actionDefByKey(step.actionKey)
    if (catalogLoaded && !action) {
      errors.push(`Azione "${step.actionKey}" non disponibile nel catalogo.`)
    }
    if (action && action.scope !== scope) {
      errors.push(`Azione "${action.label}" non compatibile con lo scope "${scope}".`)
    }
    if (action) {
      (action.params ?? []).forEach(param => {
        if (!param.required) return
        const value = step.params?.[param.key]
        if (value === undefined || value === null || String(value).trim() === '') {
          errors.push(`Parametro "${param.label}" obbligatorio per l'azione "${action.label}".`)
        }
      })
    }
    const outputs = actionOutputs(step.actionKey)
    const allowed = new Set(outputs.map(output => output.key))
    ;(step.transitions ?? []).forEach(transition => {
      if (transition.on && !allowed.has(transition.on)) {
        errors.push(`Transizione con esito "${transition.on}" non valido per l'azione "${step.actionKey}".`)
      }
      if (transition.target && !stepIds.has(transition.target)) {
        errors.push(`Transizione non valida nello step "${step.actionKey}": destinazione inesistente.`)
      }
      if (transition.target && transition.target === step.id) {
        errors.push(`Transizione non valida nello step "${step.actionKey}": lo step punta a se stesso.`)
      }
    })
  })
  if (definition.entryStepId && !stepIds.has(definition.entryStepId)) {
    errors.push('Punto di ingresso del workflow inesistente.')
  }

  return errors
}

/** Suddivide la lista destinatari (separata da virgola) in indirizzi unici. */
export function parseRecipients (value?: string): string[] {
  return [...new Set(
    (value ?? '')
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
  )]
}

export function isValidEmail (value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

/**
 * Costruisce il payload di run da una definizione (usato sia dal builder sia
 * per lanciare un template/azione direttamente su un frazionario).
 */
export function buildRunRequestPayload (
  definition: WorkflowDefinition,
  options: { runName: string; mode: 'now' | 'scheduled'; scheduledAt?: string | null }
): WorkflowRunRequest {
  return {
    run_name: options.runName.trim(),
    execution_mode: options.mode,
    scheduled_at: options.scheduledAt ?? null,
    scope: definition.scope,
    entry_step_id: definition.entryStepId ?? null,
    fraz: [...definition.fractionIds],
    actions: [...definition.steps]
      .sort((a, b) => a.order - b.order)
      .map(step => {
        const action = actionDefByKey(step.actionKey)
        const outputs = actionOutputs(step.actionKey)
        return {
          key: step.actionKey,
          type: action?.type ?? step.actionKey,
          name: action?.label ?? step.actionKey,
          step_id: step.id,
          params: { ...step.params },
          outcomes: outputs.map(output => ({
            key: output.key,
            label: output.label,
            severity: output.severity,
            addTags: [...(step.outcomes?.[output.key]?.addTags ?? [])],
            removeTags: [...(step.outcomes?.[output.key]?.removeTags ?? [])]
          })),
          post_actions: step.postActions
            .filter(post => post.enabled)
            .map(post => post.key === 'send-email'
              ? {
                  key: post.key,
                  emails: parseRecipients(post.recipients),
                  subject: post.subject?.trim() || undefined,
                  body: post.body?.trim() || undefined
                }
              : { key: post.key }),
          transitions: (step.transitions ?? []).map(transition => ({ on: transition.on, target: transition.target }))
        }
      })
  }
}
